import type { Testimonial } from 'types/testimonial'

export const testimonials: Testimonial[] = [
  {
    slug: 'lobbybar',
    company: 'LobbyBar',
    sector: 'Bar & Restaurant',
    iconName: 'ph:beer-stein-duotone',
    location: 'La Maladière Centre, Neuchâtel, Suisse',
    websiteUrl: 'https://www.lobbybar.ch/',
    websiteLabel: 'lobbybar.ch',
    logoUrl: '/testimonials/lobbybar/logo.webp',
    person: {
      name: 'Florian Truffer',
      role: 'Gérant du LobbyBar'
    },
    headline: 'Des écrans qui tournent 24h/24 et se mettent à jour à distance, en direct, sans interrompre l\'affichage ni laisser voir la moindre manipulation.',
    intro: 'Un lieu 100 % événementiel où chaque soirée a son ambiance. Les écrans doivent suivre le rythme des matchs, des séminaires et des soirées privées, sans jamais rester figés.',
    stats: [
      {
        title: '3 écrans qui captent l\'attention',
        description: 'Gérés à distance et mis à jour en direct, avec le logiciel Live Display.'
      },
      {
        title: '4 à 8 événements par mois',
        description: 'Chaque match, séminaire ou soirée a son diaporama, prêt en quelques minutes.'
      },
      {
        title: '1 an d\'utilisation au quotidien',
        description: 'Un affichage fiable, 24h/24, épaulé par un support suisse qui répond vite.'
      }
    ],
    context: {
      before: 'Avant Live Display, tout passait par PowerPoint puis OptiSign\u00A0: des outils rigides, où chaque image se calait au pixel près et où le diaporama devait être préparé à l\'avance. Et en cas de souci, personne à joindre.',
      trigger: 'Le vrai déclencheur\u00A0: pouvoir compter sur un éditeur suisse, joignable, qui répond vite et adapte son outil à chaque établissement.',
      quote: {
        text: 'Avec OptiSign, pour aligner deux images, il fallait qu\'elles fassent exactement la même taille. C\'était laborieux.',
        author: 'Florian Truffer',
        role: 'À propos d\'OptiSign'
      }
    },
    declicQuote: {
      text: 'J\'ai directement affaire au développeur qui est en face de moi. Amener des idées, remonter les bugs, c\'est beaucoup plus simple qu\'avec quelqu\'un à l\'autre bout de la planète.',
      author: 'Florian Truffer',
      role: 'Sur le choix d\'une solution locale'
    },
    impactSummary: 'Aujourd\'hui, les écrans tournent 24h/24 sans surveillance. Les mises à jour des menus et des promotions se font en quelques minutes, à distance, sans jamais couper l\'affichage. Et quand un doute survient, un appel suffit.',
    quote: {
      text: 'C\'est plus intuitif, plus naturel. La modification, c\'est devenu facile grâce à Live Display.',
      author: 'Florian Truffer',
      role: 'Gérant du LobbyBar'
    },
    heroMedia: {
      type: 'image',
      src: '/testimonials/lobbybar/hero.webp',
      alt: 'Écrans Live Display en situation au LobbyBar de Neuchâtel'
    },
    media: [
      { type: 'image', src: '/testimonials/lobbybar/image.webp', alt: 'Écrans diffusant les événements organisés au LobbyBar', caption: 'Écrans diffusant les événements organisés au LobbyBar' },
      { type: 'video', src: '/testimonials/lobbybar/video.webm', alt: 'Contenu vidéo événementiel diffusé au LobbyBar', caption: 'Contenu vidéo événementiel diffusé au LobbyBar' }
    ],
    relatedLink: '/temoignages',
    relatedLabel: 'Lire plus de témoignages',
    publishedDate: '2026-07-15',
    seoTitle: 'Témoignage LobbyBar (Neuchâtel) — affichage dynamique événementiel',
    seoDescription: 'Comment le LobbyBar, bar 100 % événementiel à Neuchâtel, gère ses 3 écrans avec le logiciel Live Display : mises à jour à distance en direct, contenu adapté à chaque soirée et support local réactif.'
  }
]

export function getTestimonialBySlug (slug: string): Testimonial | undefined {
  return testimonials.find(testimonial => testimonial.slug === slug)
}

export function getTestimonialListJsonLd (baseUrl: string): Record<string, unknown> {
  const trimmedBase = baseUrl.replace(/\/+$/, '')

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: testimonials.map((testimonial, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${trimmedBase}/temoignages/${testimonial.slug}`,
      name: testimonial.company
    }))
  }
}

export function getTestimonialVideoJsonLd (baseUrl: string, testimonial: Testimonial): Record<string, unknown>[] {
  const trimmedBase = baseUrl.replace(/\/+$/, '')
  const videos = testimonial.media?.filter(media => media.type === 'video') ?? []
  const fallbackThumbnail = testimonial.heroMedia.type === 'image' ? testimonial.heroMedia.src : undefined

  return videos.map(video => {
    const thumbnail = video.poster ?? fallbackThumbnail

    return {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.caption ?? video.alt,
      description: video.alt,
      contentUrl: `${trimmedBase}${video.src}`,
      thumbnailUrl: thumbnail ? `${trimmedBase}${thumbnail}` : undefined,
      uploadDate: testimonial.publishedDate,
      inLanguage: 'fr-CH'
    }
  })
}
